import React, { FunctionComponent, useEffect, useState } from "react";

import { bridge } from "@multiweb/reactCore/bridge";
import { OWDataType } from "@multiweb/types/contracts";
import { IEnum } from "@multiweb/types/multiweb";
import { CheckBoxIcon } from "../CheckBoxIcon";
import { IconWithBackgroundAttr } from "../IconWithBackgroundAttr";
import { NumberValue } from "../NumberValue";
import { Text } from "../Text";
import { BackgroundAttributeProps } from "../types";
import { displayDate } from "./utils";

const { getDefaultNumberFormat, stripHTMLFromText } = bridge;

export type AttributeProps = {
    /** Hodnota záznamu. */
    value: any;
    /** Datový typ atributu. */
    type?: OWDataType;
    /** Formát, v jakém má být hodnota zobrazena. */
    displayFormat?: string | null;
    /** Určuje, zda je komponenta vizuálně aktivní a zda s ní lze pracovat. */
    interactive?: boolean;
    /** Popisek */
    title?: string;
    /** Příznak, zda se má text ořezávat. */
    ellipsis?: boolean;
} & BackgroundAttributeProps;

/**
 * Komponenta vykreslí příslušnou komponentu na základě datového typu atributu.
 */
export const Attribute: FunctionComponent<AttributeProps> = ({
    value,
    type,
    displayFormat = null,
    interactive = true,
    title,
    hasBackgroundAttr = false,
    ellipsis = true,
}) => {
    const initialValue = value instanceof IEnum ? value.toLocaleString() : value;
    const [enumValue, setEnumValue] = useState(initialValue);

    useEffect(() => {
        // neměly by nastat případy, kdy je v atributu původně IEnum a typ objektu se pak změní
        if (value instanceof IEnum) {
            const fetchLocalizedName = async () => {
                const result = await value.getLocalizedName();
                setEnumValue(result);
            };

            fetchLocalizedName();
        }
    }); // žádné dependencies, value se sama o sobě nemění (jen výsledek metody getLocalizedName)

    const modifiedType = type?.startsWith("Enum|") ? "Enum" : type;
    switch (modifiedType) {
        case OWDataType.date:
        case OWDataType.time:
        case OWDataType.dateTime: {
            return displayDate(value, modifiedType, displayFormat, interactive, ellipsis);
        }
        case OWDataType.boolean: {
            return <CheckBoxIcon checked={!!value} disabled={!interactive} {...{ title }} />;
        }
        case OWDataType.icon: {
            return (
                <IconWithBackgroundAttr
                    iconCode={value}
                    disabled={!interactive}
                    {...{ title, hasBackgroundAttr }}
                />
            );
        }
        case OWDataType.byte:
        case OWDataType.short:
        case OWDataType.integer:
        case OWDataType.int64:
        case OWDataType.bcd:
        case OWDataType.double:
        case OWDataType.single:
        case OWDataType.decimal:
        case OWDataType.money: {
            return (
                <NumberValue
                    value={value}
                    displayFormat={getDefaultNumberFormat(type)}
                    disabled={!interactive}
                    title={value}
                    {...{ ellipsis }}
                />
            );
        }
        case OWDataType.flags:
        case OWDataType.enum: {
            return (
                <Text
                    value={enumValue}
                    disabled={!interactive}
                    title={enumValue}
                    {...{ ellipsis }}
                />
            );
        }
        case OWDataType.htmlText: {
            const attrValue = stripHTMLFromText(value.toString());
            return (
                <Text
                    value={attrValue}
                    disabled={!interactive}
                    title={attrValue}
                    {...{ ellipsis }}
                />
            );
        }
        default:
            return (
                <Text
                    value={value.toString()}
                    disabled={!interactive}
                    title={value.toString()}
                    {...{ ellipsis }}
                />
            );
    }
};
